import { Button } from '@/components/ui/button';
import { EditorOperatorEnum } from '@/state/editor-store';

export const BUTTON_STYLES = {
  base: `
    flex justify-start align-top bg-gradient-to-br from-[#383a3c] to-[#2b2e2f] rounded border border-[#46494c] opacity-80
    hover:opacity-100 h-[72px] transition-opacity duration-200
  `,
  active: 'from-[#ff5b27] to-[#c24017] bg-gradient-to-r ring-1 ring-zinc-400',
  inactive: 'button-shadow',
} as const;

type BaseButtonProps = React.ComponentProps<typeof Button>;

type Alignment = 'start' | 'center' | 'end';

interface GradientButtonProps extends BaseButtonProps {
  isActive?: boolean;
  width?: string;
  icon?: React.ReactNode;
  text?: string | React.ReactNode;
  isDisabled?: boolean;
  align?: Alignment;
}

interface EditorButtonProps extends Omit<GradientButtonProps, 'isActive'> {
  config: {
    key: EditorOperatorEnum;
    text: string;
    span: number;
    icon: React.ReactNode;
  };
  isSelected: boolean;
  duration?: number;
  minDuration: number;
  disabledOperations: EditorOperatorEnum[];
}

export const GradientButton = ({
  isActive = false,
  width = '100%',
  icon,
  text,
  isDisabled,
  className,
  align = 'start',
  ...props
}: GradientButtonProps) => {
  return (
    <Button
      variant="ghost"
      type="button"
      className={`
        ${BUTTON_STYLES.base}
        ${isActive ? BUTTON_STYLES.active : BUTTON_STYLES.inactive}
        ${className}
      `}
      style={{ width, ...props.style }}
      {...props}
    >
      <div className={`flex flex-col w-full items-${align}`}>
        <div>{icon}</div>
        <div
          className="mt-3 text-xs font-normal uppercase leading-none whitespace-nowrap"
          style={{
            color: isDisabled ? '#4a4d4f' : '#c3c9ce',
          }}
        >
          {text}
        </div>
      </div>
    </Button>
  );
};

export const EditorButton = ({ config, isSelected, duration, minDuration, disabledOperations, ...props }: EditorButtonProps) => {
  const isDisabled = duration && duration < minDuration && disabledOperations.includes(config.key);

  return (
    <GradientButton
      isActive={isSelected}
      width={`${(config.span / 13) * 100}%`}
      icon={config.icon}
      text={config.text}
      isDisabled={Boolean(isDisabled)}
      {...props}
    />
  );
};
